import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

const toolCallLabels = {
  TavilySearchResults: 'Haetaan tietoa',
  calculateCommission: 'Lasketaan komissiota',
  calculateRecruitmentFee: 'Lasketaan rekrytointimaksua',
  searchTalents: 'Haetaan talentteja',
};

const toolCallIcons = {
  TavilySearchResults: '🔍',
  calculateCommission: '💰',
  calculateRecruitmentFee: '💼',
  searchTalents: '👥',
};

const Loader = () => (
  <span className="inline-block align-middle ml-2">
    <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-primary rounded-full"></span>
  </span>
);

const ToolCallMessage = ({ content, result }) => {
  const [showDetails, setShowDetails] = useState(false);
  const icon = toolCallIcons[content.name] || '🛠️';
  const label = toolCallLabels[content.name] || `${content.name}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-lg">{icon}</span>
        <span className="font-medium">
          {label}
          {!result && <Loader />}
        </span>
      </div>
     
      <button
        className="text-xs text-muted-foreground underline hover:text-primary mt-1"
        onClick={() => setShowDetails((v) => !v)}
        type="button"
      >
        {showDetails ? 'Piilota tiedot' : 'Näytä tiedot'}
      </button>
      {showDetails && (
        <div className="mt-1 p-2 bg-muted/50 rounded">
          <div className="text-xs font-semibold mb-1">Tool call args:</div>
          <pre className="whitespace-pre-wrap text-xs mb-2">{JSON.stringify(content.args, null, 2)}</pre>
          {result && (
            <>
              <div className="text-xs font-semibold mb-1">Tool result:</div>
              <pre className="whitespace-pre-wrap text-xs">{result}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  const renderContent = (content) => {
    if (message.type === 'tool_call') {
      const result = message.result ?? (content && content.result);
      return <ToolCallMessage content={content} result={result} />;
    }

    if (typeof content === 'string') {
      const components = {
        p: ({ children }) => <p className="whitespace-pre-wrap m-0">{children}</p>,
        a: ({ href, children }) => (
          <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
        code: ({ node, inline, className, children, ...props }) => (
          inline ? (
            <code className="bg-muted px-1 py-0.5 rounded text-sm" {...props}>
              {children}
            </code>
          ) : (
            <pre className="bg-muted p-2 rounded-lg overflow-x-auto my-2">
              <code className="text-sm" {...props}>
                {children}
              </code>
            </pre>
          )
        ),
        ul: ({ children }) => <ul className="list-disc pl-4 my-2">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 my-2">{children}</ol>,
        li: ({ children }) => <li className="my-1">{children}</li>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-muted-foreground pl-4 my-2 italic">
            {children}
          </blockquote>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-2">
            <table className="border-collapse border border-muted-foreground">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-muted-foreground px-4 py-2 bg-muted">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-muted-foreground px-4 py-2">
            {children}
          </td>
        ),
      };

      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeSanitize]}
          components={components}
        >
          {content}
        </ReactMarkdown>
      );
    }

    if (Array.isArray(content)) {
      return content.map((item, idx) => (
        <p key={idx} className="whitespace-pre-wrap m-0">
          {typeof item === 'string' ? item : JSON.stringify(item)}
        </p>
      ));
    }

    if (content && content.error) {
      return <span className="text-destructive">Virhe: {content.error}</span>;
    }

    return null;
  };

  return (
    <div className={cn(
      "flex items-end gap-2 w-full",
      isUser ? "justify-end" : "justify-start"
    )}>
      <div className={cn(
        "max-w-[70%]",
        isUser
          ? "bg-primary text-primary-foreground rounded-xl rounded-br-none shadow p-3"
          : "bg-muted/50 text-foreground rounded-xl rounded-bl-none p-3"
      )}>
        <div className="text-xs font-semibold mb-1">
          {isUser ? "Sinä" : isSystem ? "Järjestelmä" : "Selene"}
        </div>
        <div className="prose prose-sm max-w-none dark:prose-invert">
          {renderContent(message.content)}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage; 